import { findFocusables, forceTabOrder } from '@udemy/design-system-utils';
import { useEffect } from 'react';
import { isElementVisibleInScrollPort } from './carousel-visibility-utils';
import { nextButtonDataAttribute, prevButtonDataAttribute, scrollPortDataAttribute } from './carousel.react-component';
/**
 * Hook to manage focus order in a carousel component.
 *
 * @remarks
 *
 * This hook enforces a tab focus sequence where focus moves from:
 * 1. Last focusable element before the carousel
 * 2. Into the focusable elements visible in the carousel scroll port
 * 3. To the post-content focusables (next button, previous button, first element after carousel)
 *
 * Post-content focusables are the next slide button, previous slide button, and the first
 * focusable element following the carousel. Any of these may be missing from the DOM.
 *
 * Note: This hook will not work if the carousel uses custom pager button components that are
 * rendered outside of the carousel container.
 *
 * @param carouselContainerRef - Ref to the outermost container of the carousel
 */

export function useCarouselFocusOrder(carouselContainerRef) {
  useEffect(() => {
    const carouselContainer = carouselContainerRef.current;

    if (!carouselContainer) {
      return;
    } // Query for the scroll port inside the carousel container


    const scrollPort = carouselContainer.querySelector(scrollPortDataAttribute.selector);

    if (!scrollPort) {
      return;
    }
    /**
     * Gets the previous button in the carousel
     */


    const getPreviousButton = () => {
      return carouselContainer.querySelector(prevButtonDataAttribute.selector);
    };
    /**
     * Gets the next button in the carousel
     */


    const getNextButton = () => {
      return carouselContainer.querySelector(nextButtonDataAttribute.selector);
    };
    /**
     * Gets all visible focusable elements in the scroll port
     */


    const getVisibleFocusables = () => {
      const scrollPortRect = scrollPort.getBoundingClientRect();
      const allFocusables = findFocusables(scrollPort);
      return allFocusables.filter(element => isElementVisibleInScrollPort(element, scrollPortRect));
    };
    /**
     * Gets the last focusable element before the carousel in the document
     */


    const getLastFocusableBeforeCarousel = () => {
      const allFocusables = findFocusables(document.body); // Find all carousel focusables to exclude them

      const carouselFocusables = findFocusables(carouselContainer); // Find the last focusable that's before the carousel and not inside it

      for (let i = allFocusables.length - 1; i >= 0; i--) {
        const element = allFocusables[i];

        if (element && !carouselFocusables.includes(element)) {
          const position = carouselContainer.compareDocumentPosition(element); // Note bitwise `&` to determine if element precedes carousel container in DOM order
          // https://developer.mozilla.org/en-US/docs/Web/API/Node/compareDocumentPosition

          if (position & Node.DOCUMENT_POSITION_PRECEDING) {
            return element;
          }
        }
      }

      return null;
    };
    /**
     * Gets the first focusable element after the carousel in the document
     */


    const getFirstFocusableAfterCarousel = () => {
      const allFocusables = findFocusables(document.body); // Find all carousel focusables to exclude them

      const carouselFocusables = findFocusables(carouselContainer); // Find the first focusable that's after the carousel and not inside it

      for (let i = 0; i < allFocusables.length; i++) {
        const element = allFocusables[i];

        if (element && !carouselFocusables.includes(element)) {
          const position = carouselContainer.compareDocumentPosition(element); // Note bitwise `&` to determine if element follows carousel container in DOM order
          // https://developer.mozilla.org/en-US/docs/Web/API/Node/compareDocumentPosition

          if (position & Node.DOCUMENT_POSITION_FOLLOWING) {
            return element;
          }
        }
      }

      return null;
    };
    /**
     * Gets the post-content focusables in order: next button, previous button, first element after carousel
     * Any of these may be missing from the DOM.
     */


    const getPostContentFocusables = () => {
      const focusables = [];
      const nextButton = getNextButton();
      const prevButton = getPreviousButton();
      const firstAfter = getFirstFocusableAfterCarousel();

      const sortByDocumentOrder = (a, b) => a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;

      if (nextButton && nextButton.tabIndex !== -1) {
        focusables.push(nextButton);
      }

      if (prevButton && prevButton.tabIndex !== -1) {
        focusables.push(prevButton);
      }

      if (firstAfter) {
        focusables.push(firstAfter);
      }

      return focusables.sort(sortByDocumentOrder);
    }; // Build forced tab orders


    const orders = [// From last element before carousel to first visible element in carousel
    [getLastFocusableBeforeCarousel, () => getVisibleFocusables()[0]], // From last visible element in carousel to first post-content focusable
    [() => getVisibleFocusables().at(-1), () => getPostContentFocusables()[0]]]; // Apply forced tab order

    const dispose = forceTabOrder(orders); // Cleanup

    return () => {
      dispose();
    };
  }, [carouselContainerRef]);
}
//# sourceMappingURL=use-carousel-focus-order.js.map